/**
 * Retro CRT television component.
 * Displays a YouTube embed inside a skeuomorphic TV frame with scanline
 * overlay, white-noise channel-change animation, power on/off toggle,
 * and channel up/down buttons. Includes a decorative stand and LED indicator.
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Power, ChevronUp, ChevronDown } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface RetroTVProps {
  videoId: string | null;
  isPlaying?: boolean;
  onChannelChange?: (direction: "up" | "down") => void;
}

const RetroTV = ({ videoId, isPlaying = false, onChannelChange }: RetroTVProps) => {
  const [isPoweredOn, setIsPoweredOn] = useState(true);
  const [isChangingChannel, setIsChangingChannel] = useState(false);

  const handleChannelChange = useCallback((direction: "up" | "down") => {
    if (!isPoweredOn || !onChannelChange) return;
    
    setIsChangingChannel(true);
    setTimeout(() => {
      onChannelChange(direction);
      setTimeout(() => setIsChangingChannel(false), 300);
    }, 400);
  }, [isPoweredOn, onChannelChange]);

  // White noise component
  const WhiteNoise = () => (
    <motion.div 
      className="absolute inset-0"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        opacity: 0.5,
      }}
      animate={{
        opacity: [0.4, 0.6, 0.45, 0.55, 0.4],
      }}
      transition={{
        duration: 0.15,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );

  return (
    <div className="relative">
      {/* TV Frame - Outer casing */}
      <div className="relative bg-gradient-to-b from-[hsl(0,0%,15%)] to-[hsl(0,0%,8%)] rounded-[24px] p-6 md:p-8 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.1)]">
        {/* Inner bezel */}
        <div className="relative bg-[hsl(0,0%,5%)] rounded-[12px] p-3 md:p-4 shadow-[inset_0_4px_20px_rgba(0,0,0,0.8)]">
          {/* Screen area */}
          <div className="relative overflow-hidden rounded-[8px] bg-[hsl(0,0%,2%)]">
            <AspectRatio ratio={16 / 9}>
              <AnimatePresence mode="wait">
                {!isPoweredOn ? (
                  /* TV Off - Black screen with slight reflection */
                  <motion.div
                    key="off"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-[hsl(0,0%,2%)]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-white/5" />
                  </motion.div>
                ) : isChangingChannel ? (
                  /* Channel changing - White noise */
                  <motion.div
                    key="noise"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    <WhiteNoise />
                  </motion.div>
                ) : videoId ? (
                  <motion.div
                    key={videoId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    {/* YouTube embed */}
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=${isPlaying ? 1 : 0}&rel=0&modestbranding=1`}
                      title={`Now playing: ${videoId}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                    {/* Scanline overlay */}
                    <div 
                      className="absolute inset-0 pointer-events-none opacity-20"
                      style={{
                        backgroundImage: `repeating-linear-gradient(
                          0deg,
                          transparent,
                          transparent 2px,
                          rgba(0,0,0,0.3) 2px,
                          rgba(0,0,0,0.3) 4px
                        )`,
                      }}
                    />
                    {/* CRT curve effect */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        boxShadow: `
                          inset 0 0 100px rgba(0,0,0,0.3),
                          inset 0 0 50px rgba(0,0,0,0.2)
                        `,
                        borderRadius: '8px',
                      }}
                    />
                  </motion.div>
                ) : (
                  /* No video selected - Static */
                  <motion.div
                    key="static"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    <WhiteNoise />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-body text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        No Signal
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </AspectRatio>
          </div>
        </div>

        {/* Control panel */}
        <div className="mt-4 md:mt-6 flex items-center justify-between px-2">
          {/* Power button */}
          <button
            onClick={() => setIsPoweredOn(!isPoweredOn)}
            aria-label={isPoweredOn ? "Turn TV off" : "Turn TV on"}
            className="group flex flex-col items-center gap-1"
          >
            <div className={`
              w-10 h-10 md:w-12 md:h-12 rounded-full 
              bg-gradient-to-b from-[hsl(0,0%,20%)] to-[hsl(0,0%,10%)] 
              shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),0_1px_0_rgba(255,255,255,0.1)] 
              flex items-center justify-center
              transition-all duration-200
              ${isPoweredOn ? 'ring-1 ring-green-500/30' : ''}
              group-hover:from-[hsl(0,0%,25%)] group-hover:to-[hsl(0,0%,15%)]
              group-active:scale-95
            `}>
              <Power size={16} className={isPoweredOn ? 'text-green-400' : 'text-muted-foreground'} />
            </div>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Power</span>
          </button>

          {/* Channel buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleChannelChange("down")}
              disabled={!isPoweredOn}
              aria-label="Previous channel"
              className="group flex flex-col items-center gap-1"
            >
              <div className={`
                w-8 h-8 md:w-10 md:h-10 rounded-full 
                bg-gradient-to-b from-[hsl(0,0%,20%)] to-[hsl(0,0%,10%)] 
                shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),0_1px_0_rgba(255,255,255,0.1)] 
                flex items-center justify-center
                transition-all duration-200
                ${!isPoweredOn ? 'opacity-50' : 'group-hover:from-[hsl(0,0%,25%)] group-hover:to-[hsl(0,0%,15%)] group-active:scale-95'}
              `}>
                <ChevronDown size={14} className="text-muted-foreground" />
              </div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Ch–</span>
            </button>

            {/* Power indicator LED */}
            <div className="flex flex-col items-center gap-1 px-2">
              <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                isPoweredOn 
                  ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' 
                  : 'bg-red-900'
              }`} />
              <span className="text-xs uppercase tracking-wider text-muted-foreground/50">•</span>
            </div>

            <button
              onClick={() => handleChannelChange("up")}
              disabled={!isPoweredOn}
              aria-label="Next channel"
              className="group flex flex-col items-center gap-1"
            >
              <div className={`
                w-8 h-8 md:w-10 md:h-10 rounded-full 
                bg-gradient-to-b from-[hsl(0,0%,20%)] to-[hsl(0,0%,10%)] 
                shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),0_1px_0_rgba(255,255,255,0.1)] 
                flex items-center justify-center
                transition-all duration-200
                ${!isPoweredOn ? 'opacity-50' : 'group-hover:from-[hsl(0,0%,25%)] group-hover:to-[hsl(0,0%,15%)] group-active:scale-95'}
              `}>
                <ChevronUp size={14} className="text-muted-foreground" />
              </div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Ch+</span>
            </button>
          </div>

          {/* Brand logo */}
          <div className="font-display text-xs md:text-sm uppercase tracking-[0.2em] text-muted-foreground/50">
            Thursday Jam TV
          </div>
        </div>
      </div>

      {/* TV stand/base */}
      <div className="mx-auto w-1/3 h-4 bg-gradient-to-b from-[hsl(0,0%,12%)] to-[hsl(0,0%,8%)] rounded-b-lg shadow-lg" />
      <div className="mx-auto w-1/2 h-2 bg-gradient-to-b from-[hsl(0,0%,10%)] to-[hsl(0,0%,6%)] rounded-b-lg" />
    </div>
  );
};

export default RetroTV;